/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, FieldGroup, Form, SvgIcon } from "core/components";
import { UpdatePriceSchema } from "core/schema";
import { IMaskInput } from "react-imask";
import DatePicker from "react-datepicker";
import moment from "moment";

const ItemPriceUpdate = ({ itemCode, itemName, currentPrice, show, onClose, onSubmit }): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting, isValidating }
  } = useForm({
    mode: "onChange",
    defaultValues: {
      perpetual: false,
      start_date: moment().toDate(),
      unit_price: parseFloat(currentPrice)
    },
    resolver: yupResolver(UpdatePriceSchema)
  });
  const startDate = watch("start_date");
  const perpetual = watch("perpetual");

  React.useEffect(() => {
    if (show) {
      reset(
        { perpetual: false, start_date: moment().toDate(), unit_price: parseFloat(currentPrice), new_price: null, end_date: null },
        { keepErrors: false }
      );
    }
  }, [show]);

  return (
    <React.StrictMode>
      <Modal dialogClassName="modal-dialog-centered" show={show}>
        <Modal.Header>{`Update Unit Price: ${itemCode} - ${itemName}`}</Modal.Header>
        <Modal.Body>
          <Form>
            <Container>
              <Row>
                <Col>
                  <Controller
                    name={`unit_price`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FieldGroup label="Current Price" errorMessage={error?.message}>
                        <div className="d-flex">
                          <span
                            className="field"
                            style={{
                              flex: 1,
                              alignItems: "center",
                              fontWeight: "bold",
                              backgroundColor: "#e9ecef",
                              border: "1px solid #ced4da",
                              whiteSpace: "nowrap",
                              paddingTop: "10px",
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0
                            }}
                          >
                            ₱
                          </span>
                          <IMaskInput
                            mask={Number}
                            padFractionalZeros={true}
                            thousandsSeparator=","
                            radix="."
                            value={field.value?.toString()}
                            unmask={true}
                            inputRef={field.ref}
                            onAccept={(value) => {
                              field.onChange(value);
                            }}
                            className="field"
                            placeholder="N/A"
                            readOnly={true}
                          />
                        </div>
                      </FieldGroup>
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Controller
                    name={`new_price`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FieldGroup label="New Price" required errorMessage={error?.message}>
                        <div className="d-flex">
                          <span
                            className="field"
                            style={{
                              flex: 1,
                              alignItems: "center",
                              fontWeight: "bold",
                              backgroundColor: "#e9ecef",
                              border: "1px solid #ced4da",
                              whiteSpace: "nowrap",
                              paddingTop: "10px",
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0
                            }}
                          >
                            ₱
                          </span>
                          <IMaskInput
                            mask={Number}
                            padFractionalZeros={true}
                            thousandsSeparator=","
                            radix="."
                            value={field.value?.toString()}
                            unmask={true}
                            inputRef={field.ref}
                            onAccept={(value) => {
                              field.onChange(value);
                            }}
                            className="field"
                            placeholder="0.00"
                            min={0}
                            max={9999999}
                          />
                        </div>
                      </FieldGroup>
                    )}
                  />
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col>
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FieldGroup label="End Date" required={!perpetual} errorMessage={error?.message}>
                        <DatePicker
                          selected={field.value}
                          onChange={field.onChange}
                          selectsEnd
                          className="field"
                          placeholderText="Select End Date"
                          minDate={startDate}
                          readOnly={perpetual}
                        />
                      </FieldGroup>
                    )}
                  />
                </Col>
                <Col>
                  <div className="d-flex">
                    <label className="checkbox-label">
                      <input {...register(`perpetual`)} className="field--checkbox" type="checkbox" />
                      <div className="checkbox mr-2">
                        <SvgIcon name="check" />
                      </div>
                      Perpetual
                    </label>
                  </div>
                </Col>
              </Row>
            </Container>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              onClose();
            }}
            icon="times"
            disabled={isSubmitting || isValidating}
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            disabled={isSubmitting || isValidating}
            title="Submit Price Update"
            onClick={() => {
              handleSubmit((data) => {
                onSubmit(data);
              })();
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ItemPriceUpdate;
